import React, { useState, useEffect, useRef } from 'react';
import './MarginTable.css';
import ADLTooltip from './ADLTooltip';

function MarginTable({ positions }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedExchanges, setExpandedExchanges] = useState({
    binance: true,
    bybit: true,
    okx: true,
    hyperliquid: true,
    lighter: true,
    extended: true
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef(null);
  
  // Column visibility state - default all visible
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('marginTableColumnVisibility');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Ensure kevMarginMetric exists in saved state (for users with old localStorage)
        if (parsed.kevMarginMetric === undefined) {
          parsed.kevMarginMetric = true;
        }
        return parsed;
      } catch (e) {
        // If parsing fails, use defaults
      }
    }
    return {
      exchange: true,
      symbol: true,
      side: true,
      marginUsed: true,
      marginPct: true,
      balance: true,
      liqPrice: true,
      adl: true,
      leverage: true,
      netExposure: true,
      kevMarginMetric: true,
    };
  });

  // Combine all positions from all exchanges
  const allPositions = [
    ...(positions?.binance?.positions || []).map(p => ({ ...p, account: 'Binance' })),
    ...(positions?.bybit?.positions || []).map(p => ({ ...p, account: 'Bybit' })),
    ...(positions?.okx?.positions || []).map(p => ({ ...p, account: 'OKX' })),
    ...(positions?.hyperliquid?.positions || []).map(p => ({ ...p, account: 'Hyperliquid' })),
    ...(positions?.lighter?.positions || []).map(p => ({ ...p, account: 'Lighter' })),
    ...(positions?.extended?.positions || []).map(p => ({ ...p, account: 'Extended' }))
  ];

  const toggleExchange = (exchange) => {
    setExpandedExchanges(prev => ({
      ...prev,
      [exchange]: !prev[exchange]
    }));
  };

  // Filter positions based on search query
  const filteredPositions = allPositions.filter(position => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      position.pair.toLowerCase().includes(query) ||
      position.account.toLowerCase().includes(query)
    );
  });

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    return num.toFixed(decimals);
  };

  const formatPrice = (num) => {
    if (num === null || num === undefined) return '-';
    // Use 3 decimals if price is below 1, otherwise 2 decimals
    const decimals = num < 1 ? 3 : 2;
    return num.toFixed(decimals);
  };

  // Notional (always positive) for display
  const getNotional = (position) => {
    if (position.notional == null) return 0;
    return Math.abs(position.notional);
  };

  // Save column visibility to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('marginTableColumnVisibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  // Close column menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnMenu(false);
      }
    };

    if (showColumnMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnMenu]);

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Column definitions with labels
  const columns = [
    { key: 'exchange', label: 'Exchange' },
    { key: 'symbol', label: 'Symbol' },
    { key: 'side', label: 'Side' },
    { key: 'marginUsed', label: 'Margin Used' },
    { key: 'marginPct', label: 'Margin %' },
    { key: 'balance', label: 'Balance' },
    { key: 'liqPrice', label: 'Liquidation Price' },
    { key: 'adl', label: 'ADL' },
    { key: 'leverage', label: 'Leverage' },
    { key: 'netExposure', label: 'Notional' },
    { key: 'kevMarginMetric', label: 'Kev M%' },
  ];

  // Group by exchange for summary
  let binancePositions = filteredPositions.filter(p => p.exchange === 'binance');
  let bybitPositions = filteredPositions.filter(p => p.exchange === 'bybit');
  let okxPositions = filteredPositions.filter(p => p.exchange === 'okx');
  let hyperliquidPositions = filteredPositions.filter(p => p.exchange === 'hyperliquid');
  let lighterPositions = filteredPositions.filter(p => p.exchange === 'lighter');
  let extendedPositions = filteredPositions.filter(p => p.exchange === 'extended');

  // Debug logging
  React.useEffect(() => {
  }, [positions, binancePositions.length, bybitPositions.length, okxPositions.length, hyperliquidPositions.length, lighterPositions.length, extendedPositions.length]);
  
  const binanceTotalMargin = binancePositions.reduce((sum, p) => sum + (p.margin_used || 0), 0);
  const binanceTotalBalance = binancePositions[0]?.balance || positions?.binance?.total_balance || 0;
  const binanceTotalMarginPct = binanceTotalBalance > 0 ? (binanceTotalMargin / binanceTotalBalance * 100) : 0;
  const binanceTotalPnL = binancePositions.reduce((sum, p) => sum + (p.unrealized_pnl || 0), 0);
  const binanceTotalEquity = binanceTotalBalance + binanceTotalPnL;
  // Calculate total maintenance margin for Kev Margin Metric
  const binanceTotalMaintMargin = binancePositions.reduce((sum, p) => {
    if (p.maint_margin !== null && p.maint_margin !== undefined) {
      return sum + p.maint_margin;
    }
    return sum;
  }, 0);
  const binanceKevMarginMetric = binanceTotalEquity > 0 ? (binanceTotalMaintMargin / binanceTotalEquity * 100) : null;

  const bybitTotalMargin = bybitPositions.reduce((sum, p) => sum + (p.margin_used || 0), 0);
  const bybitTotalBalance = bybitPositions[0]?.balance || positions?.bybit?.total_balance || 0;
  const bybitTotalMarginPct = bybitTotalBalance > 0 ? (bybitTotalMargin / bybitTotalBalance * 100) : 0;
  const bybitTotalPnL = bybitPositions.reduce((sum, p) => sum + (p.unrealized_pnl || 0), 0);

  const okxTotalMargin = okxPositions.reduce((sum, p) => sum + (p.margin_used || 0), 0);
  const okxTotalBalance = okxPositions[0]?.balance || positions?.okx?.total_balance || 0;
  const okxTotalMarginPct = okxTotalBalance > 0 ? (okxTotalMargin / okxTotalBalance * 100) : 0;
  const okxTotalPnL = okxPositions.reduce((sum, p) => sum + (p.unrealized_pnl || 0), 0);

  const hyperliquidTotalMargin = hyperliquidPositions.reduce((sum, p) => sum + (p.margin_used || 0), 0);
  const hyperliquidTotalBalance = hyperliquidPositions[0]?.balance || positions?.hyperliquid?.total_balance || 0;
  const hyperliquidTotalMarginPct = hyperliquidTotalBalance > 0 ? (hyperliquidTotalMargin / hyperliquidTotalBalance * 100) : 0;
  const hyperliquidTotalPnL = hyperliquidPositions.reduce((sum, p) => sum + (p.unrealized_pnl || 0), 0);
  const hyperliquidTotalEquity = hyperliquidTotalBalance + hyperliquidTotalPnL;
  // Calculate total Kev Margin Metric from positions (already calculated per position)
  const hyperliquidKevMarginMetrics = hyperliquidPositions.map(p => p.kev_margin_metric).filter(m => m !== null && m !== undefined);
  const hyperliquidKevMarginMetric = hyperliquidKevMarginMetrics.length > 0 
    ? hyperliquidKevMarginMetrics.reduce((sum, m) => sum + m, 0) / hyperliquidKevMarginMetrics.length 
    : null;

  const lighterTotalMargin = lighterPositions.reduce((sum, p) => sum + (p.margin_used || 0), 0);
  const lighterTotalBalance = lighterPositions[0]?.balance || positions?.lighter?.total_balance || 0;
  const lighterTotalMarginPct = lighterTotalBalance > 0 ? (lighterTotalMargin / lighterTotalBalance * 100) : 0;
  const lighterTotalPnL = lighterPositions.reduce((sum, p) => sum + (p.unrealized_pnl || 0), 0);
  const lighterTotalEquity = lighterTotalBalance + lighterTotalPnL;
  // Calculate total Kev Margin Metric from positions (already calculated per position)
  const lighterKevMarginMetrics = lighterPositions.map(p => p.kev_margin_metric).filter(m => m !== null && m !== undefined);
  const lighterKevMarginMetric = lighterKevMarginMetrics.length > 0 
    ? lighterKevMarginMetrics.reduce((sum, m) => sum + m, 0) / lighterKevMarginMetrics.length 
    : null;

  const extendedTotalMargin = extendedPositions.reduce((sum, p) => sum + (p.margin_used || 0), 0);
  const extendedTotalBalance = extendedPositions[0]?.balance || positions?.extended?.total_balance || 0;
  const extendedTotalMarginPct = extendedTotalBalance > 0 ? (extendedTotalMargin / extendedTotalBalance * 100) : 0;
  const extendedTotalPnL = extendedPositions.reduce((sum, p) => sum + (p.unrealized_pnl || 0), 0);
  const extendedTotalEquity = extendedTotalBalance + extendedTotalPnL;
  // Calculate total Kev Margin Metric from positions (already calculated per position)
  const extendedKevMarginMetrics = extendedPositions.map(p => p.kev_margin_metric).filter(m => m !== null && m !== undefined);
  const extendedKevMarginMetric = extendedKevMarginMetrics.length > 0 
    ? extendedKevMarginMetrics.reduce((sum, m) => sum + m, 0) / extendedKevMarginMetrics.length 
    : null;

  if (allPositions.length === 0) {
    return (
      <div className="margin-table-container">
        <p>No positions to display margin information</p>
      </div>
    );
  }

  const getMarginClass = (percentage) => {
    if (percentage > 80) return 'high'; // Red - high risk
    if (percentage >= 50) return 'medium'; // Orange - warning (50% and above)
    return 'low'; // Green - safe (below 50%)
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortPositions = (positions) => {
    if (!sortConfig.key) return positions;
    
    const sorted = [...positions].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle nested properties
      if (sortConfig.key === 'pair') {
        aVal = a.pair?.toLowerCase() || '';
        bVal = b.pair?.toLowerCase() || '';
      } else if (sortConfig.key === 'account') {
        aVal = a.account?.toLowerCase() || '';
        bVal = b.account?.toLowerCase() || '';
      } else if (sortConfig.key === 'side') {
        aVal = a.side?.toLowerCase() || '';
        bVal = b.side?.toLowerCase() || '';
      }
      
      // Handle ADL specifically
      if (sortConfig.key === 'adl') {
        aVal = a.adl || 999; // Put positions without ADL at the end
        bVal = b.adl || 999;
      }
      
      // Handle notional (sort by absolute value)
      if (sortConfig.key === 'net_exposure') {
        aVal = getNotional(a);
        bVal = getNotional(b);
      }
      
      // Handle numeric comparisons
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle string comparisons
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  binancePositions = sortPositions(binancePositions);
  hyperliquidPositions = sortPositions(hyperliquidPositions);
  lighterPositions = sortPositions(lighterPositions);
  extendedPositions = sortPositions(extendedPositions);

  return (
    <div className="margin-table-container">
      <div className="table-header">
        <h2>Margin Overview ({filteredPositions.length})</h2>
        <div className="search-container">
          <svg className="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 14L11.1 11.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Search by symbol, exchange..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="column-visibility-container" ref={columnMenuRef}>
            <button
              className="column-visibility-button"
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              title="Customize columns"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="4" r="1.5" fill="currentColor"/>
                <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                <circle cx="8" cy="12" r="1.5" fill="currentColor"/>
              </svg>
            </button>
            {showColumnMenu && (
              <div className="column-menu">
                <div className="column-menu-header">Customize Columns</div>
                <div className="column-menu-list">
                  {columns.map(column => (
                    <label key={column.key} className="column-menu-item">
                      <input
                        type="checkbox"
                        checked={columnVisibility[column.key]}
                        onChange={() => toggleColumn(column.key)}
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="margin-table">
          <thead>
            <tr>
              {columnVisibility.exchange && (
                <th onClick={() => handleSort('account')} className="sortable-header">
                  Exchange {sortConfig.key === 'account' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.symbol && (
                <th onClick={() => handleSort('pair')} className="sortable-header">
                  Symbol {sortConfig.key === 'pair' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.side && (
                <th onClick={() => handleSort('side')} className="sortable-header">
                  Side {sortConfig.key === 'side' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.marginUsed && (
                <th onClick={() => handleSort('margin_used')} className="sortable-header">
                  Margin Used {sortConfig.key === 'margin_used' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.marginPct && (
                <th onClick={() => handleSort('margin_percentage')} className="sortable-header">
                  Margin % {sortConfig.key === 'margin_percentage' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.balance && (
                <th onClick={() => handleSort('balance')} className="sortable-header">
                  Balance {sortConfig.key === 'balance' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.liqPrice && (
                <th onClick={() => handleSort('liquidation_price')} className="sortable-header">
                  Liquidation Price {sortConfig.key === 'liquidation_price' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.adl && (
                <th onClick={() => handleSort('adl')} className="sortable-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    ADL {sortConfig.key === 'adl' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    <ADLTooltip />
                  </div>
                </th>
              )}
              {columnVisibility.leverage && (
                <th onClick={() => handleSort('leverage')} className="sortable-header">
                  Leverage {sortConfig.key === 'leverage' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.netExposure && (
                <th onClick={() => handleSort('net_exposure')} className="sortable-header">
                  Notional {sortConfig.key === 'net_exposure' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
              {columnVisibility.kevMarginMetric && (
                <th onClick={() => handleSort('kev_margin_metric')} className="sortable-header">
                  Kev M% {sortConfig.key === 'kev_margin_metric' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredPositions.length === 0 ? (
              <tr>
                <td colSpan={columns.filter(c => columnVisibility[c.key]).length} className="no-results">
                  No positions found matching "{searchQuery}"
                </td>
              </tr>
            ) : (
              <>
                {/* Binance Section */}
                {binancePositions.length > 0 && (
                  <>
                    <tr 
                      className="exchange-header-row"
                      onClick={() => toggleExchange('binance')}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="exchange-header-cell">
                        <div className="exchange-header-content">
                          <svg 
                            className={`toggle-icon ${expandedExchanges.binance ? 'expanded' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12" 
                            fill="none"
                          >
                            <path 
                              d="M3 4.5L6 7.5L9 4.5" 
                              stroke="currentColor" 
                              strokeWidth="1.5" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span className="exchange-name">Binance</span>
                        </div>
                      </td>
                      <td className="exchange-header-cell" colSpan={columnVisibility.symbol && columnVisibility.side ? 2 : (columnVisibility.symbol || columnVisibility.side ? 1 : 0)}>
                        <span className="exchange-summary">
                          Total Margin: ${formatNumber(binanceTotalMargin)} ({formatNumber(binanceTotalMarginPct)}%) | 
                          Balance: ${formatNumber(binanceTotalBalance)} | 
                          Unrealized PnL: <span className={binanceTotalPnL >= 0 ? 'pnl-positive' : 'pnl-negative'}>
                            {binanceTotalPnL >= 0 ? '+' : ''}${formatNumber(binanceTotalPnL)}
                          </span>
                        </span>
                      </td>
                      {columnVisibility.marginUsed && <td className="exchange-header-cell"></td>}
                      {columnVisibility.marginPct && (
                        <td className={`exchange-header-cell margin-pct-cell ${getMarginClass(binanceTotalMarginPct)}`}>
                          <div className="margin-pct-value">{formatNumber(binanceTotalMarginPct)}%</div>
                          <div className="margin-bar-container">
                            <div className="margin-bar-threshold"></div>
                            <div 
                              className={`margin-bar ${getMarginClass(binanceTotalMarginPct)}`}
                              style={{ width: `${Math.min(binanceTotalMarginPct, 100)}%` }}
                            />
                          </div>
                        </td>
                      )}
                      {columnVisibility.balance && <td className="exchange-header-cell balance-cell">${formatNumber(binanceTotalBalance)}</td>}
                      {columnVisibility.liqPrice && <td className="exchange-header-cell"></td>}
                      {columnVisibility.adl && <td className="exchange-header-cell"></td>}
                      {columnVisibility.leverage && <td className="exchange-header-cell"></td>}
                      {columnVisibility.netExposure && <td className="exchange-header-cell"></td>}
                      {columnVisibility.kevMarginMetric && (
                        <td className={`exchange-header-cell margin-pct-cell ${getMarginClass(binanceKevMarginMetric || 0)}`}>
                          {binanceKevMarginMetric !== null && binanceKevMarginMetric !== undefined 
                            ? `${formatNumber(binanceKevMarginMetric)}%` 
                            : '-'}
                        </td>
                      )}
                    </tr>
                    {expandedExchanges.binance && binancePositions.map((position, idx) => {
                      const marginClass = getMarginClass(position.margin_percentage || 0);
                      const notional = getNotional(position);
                      return (
                        <tr key={`binance-${position.pair}-${idx}`} className="margin-row">
                          {columnVisibility.exchange && (
                            <td className="exchange-cell">Binance</td>
                          )}
                          {columnVisibility.symbol && (
                            <td className="symbol-cell">
                              <div className="symbol-info">
                                <span className={`side-indicator ${position.side.toLowerCase()}`}></span>
                                <span className="symbol-name">{position.pair}</span>
                              </div>
                            </td>
                          )}
                          {columnVisibility.side && (
                            <td className={`side-cell ${position.side.toLowerCase()}`}>
                              <span className="side-cell-with-bar">
                                <span className="side-bar" aria-hidden="true"></span>
                                <span>{position.side}</span>
                              </span>
                            </td>
                          )}
                          {columnVisibility.marginUsed && (
                            <td className="margin-used-cell">
                              ${formatNumber(position.margin_used)}
                            </td>
                          )}
                          {columnVisibility.marginPct && (
                            <td className={`margin-pct-cell ${marginClass}`}>
                              <div className="margin-pct-value">{formatNumber(position.margin_percentage)}%</div>
                              <div className="margin-bar-container">
                                <div className="margin-bar-threshold"></div>
                                <div 
                                  className={`margin-bar ${marginClass}`}
                                  style={{ width: `${Math.min(position.margin_percentage || 0, 100)}%` }}
                                />
                              </div>
                            </td>
                          )}
                          {columnVisibility.balance && <td className="balance-cell">–</td>}
                          {columnVisibility.liqPrice && (
                            <td className="liq-price-cell">
                              {position.liquidation_price ? `$${formatPrice(position.liquidation_price)}` : '-'}
                            </td>
                          )}
                          {columnVisibility.adl && (
                            <td className="adl-cell">
                              {position.adl !== null && position.adl !== undefined ? `${position.adl}/5` : '-'}
                            </td>
                          )}
                          {columnVisibility.leverage && (
                            <td className="leverage-cell">{position.leverage}x</td>
                          )}
                          {columnVisibility.netExposure && (
                            <td className="net-exposure-cell">
                              ${formatNumber(notional)}
                            </td>
                          )}
                          {columnVisibility.kevMarginMetric && (
                            <td className={`margin-pct-cell ${getMarginClass(position.kev_margin_metric || 0)}`}>
                              {position.kev_margin_metric !== null && position.kev_margin_metric !== undefined 
                                ? `${formatNumber(position.kev_margin_metric)}%` 
                                : '-'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </>
                )}

                {/* Bybit Section */}
                {bybitPositions.length > 0 && (
                  <>
                    <tr 
                      className="exchange-header-row"
                      onClick={() => toggleExchange('bybit')}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="exchange-header-cell">
                        <div className="exchange-header-content">
                          <svg 
                            className={`toggle-icon ${expandedExchanges.bybit ? 'expanded' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12" 
                            fill="none"
                          >
                            <path 
                              d="M3 4.5L6 7.5L9 4.5" 
                              stroke="currentColor" 
                              strokeWidth="1.5" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span className="exchange-name">Bybit</span>
                        </div>
                      </td>
                      <td className="exchange-header-cell" colSpan={columnVisibility.symbol && columnVisibility.side ? 2 : (columnVisibility.symbol || columnVisibility.side ? 1 : 0)}>
                        <span className="exchange-summary">
                          Total Margin: ${formatNumber(bybitTotalMargin)} ({formatNumber(bybitTotalMarginPct)}%) | 
                          Balance: ${formatNumber(bybitTotalBalance)} | 
                          Unrealized PnL: <span className={bybitTotalPnL >= 0 ? 'pnl-positive' : 'pnl-negative'}>
                            {bybitTotalPnL >= 0 ? '+' : ''}${formatNumber(bybitTotalPnL)}
                          </span>
                        </span>
                      </td>
                      {columnVisibility.marginUsed && <td className="exchange-header-cell"></td>}
                      {columnVisibility.marginPct && (
                        <td className={`exchange-header-cell margin-pct-cell ${getMarginClass(bybitTotalMarginPct)}`}>
                          <div className="margin-pct-value">{formatNumber(bybitTotalMarginPct)}%</div>
                          <div className="margin-bar-container">
                            <div className="margin-bar-threshold"></div>
                            <div 
                              className={`margin-bar ${getMarginClass(bybitTotalMarginPct)}`}
                              style={{ width: `${Math.min(bybitTotalMarginPct, 100)}%` }}
                            />
                          </div>
                        </td>
                      )}
                      {columnVisibility.balance && <td className="exchange-header-cell balance-cell">${formatNumber(bybitTotalBalance)}</td>}
                      {columnVisibility.liqPrice && <td className="exchange-header-cell"></td>}
                      {columnVisibility.adl && <td className="exchange-header-cell"></td>}
                      {columnVisibility.leverage && <td className="exchange-header-cell"></td>}
                      {columnVisibility.netExposure && <td className="exchange-header-cell"></td>}
                      {columnVisibility.kevMarginMetric && <td className="exchange-header-cell"></td>}
                    </tr>
                    {expandedExchanges.bybit && bybitPositions.map((position, idx) => {
                      const marginClass = getMarginClass(position.margin_percentage || 0);
                      const notional = getNotional(position);
                      return (
                        <tr key={`bybit-${position.pair}-${idx}`} className="margin-row">
                          {columnVisibility.exchange && (
                            <td className="exchange-cell">Bybit</td>
                          )}
                          {columnVisibility.symbol && (
                            <td className="symbol-cell">
                              <div className="symbol-info">
                                <span className={`side-indicator ${position.side.toLowerCase()}`}></span>
                                <span className="symbol-name">{position.pair}</span>
                              </div>
                            </td>
                          )}
                          {columnVisibility.side && (
                            <td className={`side-cell ${position.side.toLowerCase()}`}>
                              <span className="side-cell-with-bar">
                                <span className="side-bar" aria-hidden="true"></span>
                                <span>{position.side}</span>
                              </span>
                            </td>
                          )}
                          {columnVisibility.marginUsed && (
                            <td className="margin-used-cell">${formatNumber(position.margin_used)}</td>
                          )}
                          {columnVisibility.marginPct && (
                            <td className={`margin-pct-cell ${marginClass}`}>
                              {formatNumber(position.margin_percentage)}%
                            </td>
                          )}
                          {columnVisibility.balance && <td className="balance-cell">–</td>}
                          {columnVisibility.liqPrice && (
                            <td className="liq-price-cell">
                              {position.liquidation_price != null ? `$${formatNumber(position.liquidation_price)}` : '-'}
                            </td>
                          )}
                          {columnVisibility.adl && (
                            <td className="adl-cell">{position.adl ?? '-'}</td>
                          )}
                          {columnVisibility.leverage && (
                            <td className="leverage-cell">{position.leverage}x</td>
                          )}
                          {columnVisibility.netExposure && (
                            <td className="net-exposure-cell">
                              ${formatNumber(notional)}
                            </td>
                          )}
                          {columnVisibility.kevMarginMetric && (
                            <td className={`margin-pct-cell ${getMarginClass(position.kev_margin_metric || 0)}`}>
                              {position.kev_margin_metric != null ? `${formatNumber(position.kev_margin_metric)}%` : '-'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </>
                )}

                {/* OKX Section */}
                {okxPositions.length > 0 && (
                  <>
                    <tr 
                      className="exchange-header-row"
                      onClick={() => toggleExchange('okx')}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="exchange-header-cell">
                        <div className="exchange-header-content">
                          <svg 
                            className={`toggle-icon ${expandedExchanges.okx ? 'expanded' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12" 
                            fill="none"
                          >
                            <path 
                              d="M3 4.5L6 7.5L9 4.5" 
                              stroke="currentColor" 
                              strokeWidth="1.5" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span className="exchange-name">OKX</span>
                        </div>
                      </td>
                      <td className="exchange-header-cell" colSpan={columnVisibility.symbol && columnVisibility.side ? 2 : (columnVisibility.symbol || columnVisibility.side ? 1 : 0)}>
                        <span className="exchange-summary">
                          Total Margin: ${formatNumber(okxTotalMargin)} ({formatNumber(okxTotalMarginPct)}%) | 
                          Balance: ${formatNumber(okxTotalBalance)} | 
                          Unrealized PnL: <span className={okxTotalPnL >= 0 ? 'pnl-positive' : 'pnl-negative'}>
                            {okxTotalPnL >= 0 ? '+' : ''}${formatNumber(okxTotalPnL)}
                          </span>
                        </span>
                      </td>
                      {columnVisibility.marginUsed && <td className="exchange-header-cell"></td>}
                      {columnVisibility.marginPct && (
                        <td className={`exchange-header-cell margin-pct-cell ${getMarginClass(okxTotalMarginPct)}`}>
                          <div className="margin-pct-value">{formatNumber(okxTotalMarginPct)}%</div>
                          <div className="margin-bar-container">
                            <div className="margin-bar-threshold"></div>
                            <div 
                              className={`margin-bar ${getMarginClass(okxTotalMarginPct)}`}
                              style={{ width: `${Math.min(okxTotalMarginPct, 100)}%` }}
                            />
                          </div>
                        </td>
                      )}
                      {columnVisibility.balance && <td className="exchange-header-cell balance-cell">${formatNumber(okxTotalBalance)}</td>}
                      {columnVisibility.liqPrice && <td className="exchange-header-cell"></td>}
                      {columnVisibility.adl && <td className="exchange-header-cell"></td>}
                      {columnVisibility.leverage && <td className="exchange-header-cell"></td>}
                      {columnVisibility.netExposure && <td className="exchange-header-cell"></td>}
                      {columnVisibility.kevMarginMetric && <td className="exchange-header-cell"></td>}
                    </tr>
                    {expandedExchanges.okx && okxPositions.map((position, idx) => {
                      const marginClass = getMarginClass(position.margin_percentage || 0);
                      const notional = getNotional(position);
                      return (
                        <tr key={`okx-${position.pair}-${idx}`} className="margin-row">
                          {columnVisibility.exchange && (
                            <td className="exchange-cell">OKX</td>
                          )}
                          {columnVisibility.symbol && (
                            <td className="symbol-cell">
                              <div className="symbol-info">
                                <span className={`side-indicator ${position.side.toLowerCase()}`}></span>
                                <span className="symbol-name">{position.pair}</span>
                              </div>
                            </td>
                          )}
                          {columnVisibility.side && (
                            <td className={`side-cell ${position.side.toLowerCase()}`}>
                              <span className="side-cell-with-bar">
                                <span className="side-bar" aria-hidden="true"></span>
                                <span>{position.side}</span>
                              </span>
                            </td>
                          )}
                          {columnVisibility.marginUsed && (
                            <td className="margin-used-cell">${formatNumber(position.margin_used)}</td>
                          )}
                          {columnVisibility.marginPct && (
                            <td className={`margin-pct-cell ${marginClass}`}>
                              {formatNumber(position.margin_percentage)}%
                            </td>
                          )}
                          {columnVisibility.balance && <td className="balance-cell">–</td>}
                          {columnVisibility.liqPrice && (
                            <td className="liq-price-cell">
                              {position.liquidation_price != null ? `$${formatNumber(position.liquidation_price)}` : '-'}
                            </td>
                          )}
                          {columnVisibility.adl && (
                            <td className="adl-cell">{position.adl ?? '-'}</td>
                          )}
                          {columnVisibility.leverage && (
                            <td className="leverage-cell">{position.leverage}x</td>
                          )}
                          {columnVisibility.netExposure && (
                            <td className="net-exposure-cell">
                              ${formatNumber(notional)}
                            </td>
                          )}
                          {columnVisibility.kevMarginMetric && (
                            <td className={`margin-pct-cell ${getMarginClass(position.kev_margin_metric || 0)}`}>
                              {position.kev_margin_metric != null ? `${formatNumber(position.kev_margin_metric)}%` : '-'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </>
                )}

                {/* Hyperliquid Section */}
                {hyperliquidPositions.length > 0 && (
                  <>
                    <tr 
                      className="exchange-header-row"
                      onClick={() => toggleExchange('hyperliquid')}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="exchange-header-cell">
                        <div className="exchange-header-content">
                          <svg 
                            className={`toggle-icon ${expandedExchanges.hyperliquid ? 'expanded' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12" 
                            fill="none"
                          >
                            <path 
                              d="M3 4.5L6 7.5L9 4.5" 
                              stroke="currentColor" 
                              strokeWidth="1.5" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span className="exchange-name">Hyperliquid</span>
                        </div>
                      </td>
                      <td className="exchange-header-cell" colSpan={columnVisibility.symbol && columnVisibility.side ? 2 : (columnVisibility.symbol || columnVisibility.side ? 1 : 0)}>
                        <span className="exchange-summary">
                          Total Margin: ${formatNumber(hyperliquidTotalMargin)} ({formatNumber(hyperliquidTotalMarginPct)}%) | 
                          Balance: ${formatNumber(hyperliquidTotalBalance)} | 
                          Unrealized PnL: <span className={hyperliquidTotalPnL >= 0 ? 'pnl-positive' : 'pnl-negative'}>
                            {hyperliquidTotalPnL >= 0 ? '+' : ''}${formatNumber(hyperliquidTotalPnL)}
                          </span>
                        </span>
                      </td>
                      {columnVisibility.marginUsed && <td className="exchange-header-cell"></td>}
                      {columnVisibility.marginPct && (
                        <td className={`exchange-header-cell margin-pct-cell ${getMarginClass(hyperliquidTotalMarginPct)}`}>
                          <div className="margin-pct-value">{formatNumber(hyperliquidTotalMarginPct)}%</div>
                          <div className="margin-bar-container">
                            <div className="margin-bar-threshold"></div>
                            <div 
                              className={`margin-bar ${getMarginClass(hyperliquidTotalMarginPct)}`}
                              style={{ width: `${Math.min(hyperliquidTotalMarginPct, 100)}%` }}
                            />
                          </div>
                        </td>
                      )}
                      {columnVisibility.balance && <td className="exchange-header-cell balance-cell">${formatNumber(hyperliquidTotalBalance)}</td>}
                      {columnVisibility.liqPrice && <td className="exchange-header-cell"></td>}
                      {columnVisibility.adl && <td className="exchange-header-cell"></td>}
                      {columnVisibility.leverage && <td className="exchange-header-cell"></td>}
                      {columnVisibility.netExposure && <td className="exchange-header-cell"></td>}
                      {columnVisibility.kevMarginMetric && (
                        <td className={`exchange-header-cell margin-pct-cell ${getMarginClass(hyperliquidKevMarginMetric || 0)}`}>
                          {hyperliquidKevMarginMetric !== null && hyperliquidKevMarginMetric !== undefined 
                            ? `${formatNumber(hyperliquidKevMarginMetric)}%` 
                            : '-'}
                        </td>
                      )}
                    </tr>
                    {expandedExchanges.hyperliquid && hyperliquidPositions.map((position, idx) => {
                      const marginClass = getMarginClass(position.margin_percentage || 0);
                      const notional = getNotional(position);
                      return (
                        <tr key={`hyperliquid-${position.pair}-${idx}`} className="margin-row">
                          {columnVisibility.exchange && (
                            <td className="exchange-cell">Hyperliquid</td>
                          )}
                          {columnVisibility.symbol && (
                            <td className="symbol-cell">
                              <div className="symbol-info">
                                <span className={`side-indicator ${position.side.toLowerCase()}`}></span>
                                <span className="symbol-name">{position.pair}</span>
                              </div>
                            </td>
                          )}
                          {columnVisibility.side && (
                            <td className={`side-cell ${position.side.toLowerCase()}`}>
                              <span className="side-cell-with-bar">
                                <span className="side-bar" aria-hidden="true"></span>
                                <span>{position.side}</span>
                              </span>
                            </td>
                          )}
                          {columnVisibility.marginUsed && (
                            <td className="margin-used-cell">
                              ${formatNumber(position.margin_used)}
                            </td>
                          )}
                          {columnVisibility.marginPct && (
                            <td className={`margin-pct-cell ${marginClass}`}>
                              <div className="margin-pct-value">{formatNumber(position.margin_percentage)}%</div>
                              <div className="margin-bar-container">
                                <div className="margin-bar-threshold"></div>
                                <div 
                                  className={`margin-bar ${marginClass}`}
                                  style={{ width: `${Math.min(position.margin_percentage || 0, 100)}%` }}
                                />
                              </div>
                            </td>
                          )}
                          {columnVisibility.balance && <td className="balance-cell">–</td>}
                          {columnVisibility.liqPrice && (
                            <td className="liq-price-cell">
                              {position.liquidation_price ? `$${formatPrice(position.liquidation_price)}` : '-'}
                            </td>
                          )}
                          {columnVisibility.adl && (
                            <td className="adl-cell">
                              {position.adl !== null && position.adl !== undefined ? `${position.adl}/5` : '-'}
                            </td>
                          )}
                          {columnVisibility.leverage && (
                            <td className="leverage-cell">{position.leverage}x</td>
                          )}
                          {columnVisibility.netExposure && (
                            <td className="net-exposure-cell">
                              ${formatNumber(notional)}
                            </td>
                          )}
                          {columnVisibility.kevMarginMetric && (
                            <td className={`margin-pct-cell ${getMarginClass(position.kev_margin_metric || 0)}`}>
                              {position.kev_margin_metric !== null && position.kev_margin_metric !== undefined 
                                ? `${formatNumber(position.kev_margin_metric)}%` 
                                : '-'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </>
                )}

                {/* Lighter Section */}
                {lighterPositions.length > 0 && (
                  <>
                    <tr 
                      className="exchange-header-row"
                      onClick={() => toggleExchange('lighter')}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="exchange-header-cell">
                        <div className="exchange-header-content">
                          <svg 
                            className={`toggle-icon ${expandedExchanges.lighter ? 'expanded' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12" 
                            fill="none"
                          >
                            <path 
                              d="M3 4.5L6 7.5L9 4.5" 
                              stroke="currentColor" 
                              strokeWidth="1.5" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span className="exchange-name">Lighter</span>
                        </div>
                      </td>
                      <td className="exchange-header-cell" colSpan={columnVisibility.symbol && columnVisibility.side ? 2 : (columnVisibility.symbol || columnVisibility.side ? 1 : 0)}>
                        <span className="exchange-summary">
                          Total Margin: ${formatNumber(lighterTotalMargin)} ({formatNumber(lighterTotalMarginPct)}%) | 
                          Balance: ${formatNumber(lighterTotalBalance)} | 
                          Unrealized PnL: <span className={lighterTotalPnL >= 0 ? 'pnl-positive' : 'pnl-negative'}>
                            {lighterTotalPnL >= 0 ? '+' : ''}${formatNumber(lighterTotalPnL)}
                          </span>
                        </span>
                      </td>
                      {columnVisibility.marginUsed && <td className="exchange-header-cell"></td>}
                      {columnVisibility.marginPct && (
                        <td className={`exchange-header-cell margin-pct-cell ${getMarginClass(lighterTotalMarginPct)}`}>
                          <div className="margin-pct-value">{formatNumber(lighterTotalMarginPct)}%</div>
                          <div className="margin-bar-container">
                            <div className="margin-bar-threshold"></div>
                            <div 
                              className={`margin-bar ${getMarginClass(lighterTotalMarginPct)}`}
                              style={{ width: `${Math.min(lighterTotalMarginPct, 100)}%` }}
                            />
                          </div>
                        </td>
                      )}
                      {columnVisibility.balance && <td className="exchange-header-cell balance-cell">${formatNumber(lighterTotalBalance)}</td>}
                      {columnVisibility.liqPrice && <td className="exchange-header-cell"></td>}
                      {columnVisibility.adl && <td className="exchange-header-cell"></td>}
                      {columnVisibility.leverage && <td className="exchange-header-cell"></td>}
                      {columnVisibility.netExposure && <td className="exchange-header-cell"></td>}
                      {columnVisibility.kevMarginMetric && (
                        <td className={`exchange-header-cell margin-pct-cell ${getMarginClass(lighterKevMarginMetric || 0)}`}>
                          {lighterKevMarginMetric !== null && lighterKevMarginMetric !== undefined 
                            ? `${formatNumber(lighterKevMarginMetric)}%` 
                            : '-'}
                        </td>
                      )}
                    </tr>
                    {expandedExchanges.lighter && lighterPositions.map((position, idx) => {
                      const marginClass = getMarginClass(position.margin_percentage || 0);
                      const notional = getNotional(position);
                      return (
                        <tr key={`lighter-${position.pair}-${idx}`} className="margin-row">
                          {columnVisibility.exchange && (
                            <td className="exchange-cell">Lighter</td>
                          )}
                          {columnVisibility.symbol && (
                            <td className="symbol-cell">
                              <div className="symbol-info">
                                <span className={`side-indicator ${position.side.toLowerCase()}`}></span>
                                <span className="symbol-name">{position.pair}</span>
                              </div>
                            </td>
                          )}
                          {columnVisibility.side && (
                            <td className={`side-cell ${position.side.toLowerCase()}`}>
                              <span className="side-cell-with-bar">
                                <span className="side-bar" aria-hidden="true"></span>
                                <span>{position.side}</span>
                              </span>
                            </td>
                          )}
                          {columnVisibility.marginUsed && (
                            <td className="margin-used-cell">
                              ${formatNumber(position.margin_used)}
                            </td>
                          )}
                          {columnVisibility.marginPct && (
                            <td className={`margin-pct-cell ${marginClass}`}>
                              <div className="margin-pct-value">{formatNumber(position.margin_percentage)}%</div>
                              <div className="margin-bar-container">
                                <div className="margin-bar-threshold"></div>
                                <div 
                                  className={`margin-bar ${marginClass}`}
                                  style={{ width: `${Math.min(position.margin_percentage || 0, 100)}%` }}
                                />
                              </div>
                            </td>
                          )}
                          {columnVisibility.balance && <td className="balance-cell">–</td>}
                          {columnVisibility.liqPrice && (
                            <td className="liq-price-cell">
                              {position.liquidation_price ? `$${formatPrice(position.liquidation_price)}` : '-'}
                            </td>
                          )}
                          {columnVisibility.adl && (
                            <td className="adl-cell">
                              {position.adl !== null && position.adl !== undefined ? `${position.adl}/5` : '-'}
                            </td>
                          )}
                          {columnVisibility.leverage && (
                            <td className="leverage-cell">{position.leverage}x</td>
                          )}
                          {columnVisibility.netExposure && (
                            <td className="net-exposure-cell">
                              ${formatNumber(notional)}
                            </td>
                          )}
                          {columnVisibility.kevMarginMetric && (
                            <td className={`margin-pct-cell ${getMarginClass(position.kev_margin_metric || 0)}`}>
                              {position.kev_margin_metric !== null && position.kev_margin_metric !== undefined 
                                ? `${formatNumber(position.kev_margin_metric)}%` 
                                : '-'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </>
                )}

                {/* Extended Section */}
                {extendedPositions.length > 0 && (
                  <>
                    <tr 
                      className="exchange-header-row"
                      onClick={() => toggleExchange('extended')}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="exchange-header-cell">
                        <div className="exchange-header-content">
                          <svg 
                            className={`toggle-icon ${expandedExchanges.extended ? 'expanded' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12" 
                            fill="none"
                          >
                            <path 
                              d="M3 4.5L6 7.5L9 4.5" 
                              stroke="currentColor" 
                              strokeWidth="1.5" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span className="exchange-name">Extended</span>
                        </div>
                      </td>
                      <td className="exchange-header-cell" colSpan={columnVisibility.symbol && columnVisibility.side ? 2 : (columnVisibility.symbol || columnVisibility.side ? 1 : 0)}>
                        <span className="exchange-summary">
                          Total Margin: ${formatNumber(extendedTotalMargin)} ({formatNumber(extendedTotalMarginPct)}%) | 
                          Balance: ${formatNumber(extendedTotalBalance)} | 
                          Unrealized PnL: <span className={extendedTotalPnL >= 0 ? 'pnl-positive' : 'pnl-negative'}>
                            {extendedTotalPnL >= 0 ? '+' : ''}${formatNumber(extendedTotalPnL)}
                          </span>
                        </span>
                      </td>
                      {columnVisibility.marginUsed && <td className="exchange-header-cell"></td>}
                      {columnVisibility.marginPct && (
                        <td className={`exchange-header-cell margin-pct-cell ${getMarginClass(extendedTotalMarginPct)}`}>
                          <div className="margin-pct-value">{formatNumber(extendedTotalMarginPct)}%</div>
                          <div className="margin-bar-container">
                            <div className="margin-bar-threshold"></div>
                            <div 
                              className={`margin-bar ${getMarginClass(extendedTotalMarginPct)}`}
                              style={{ width: `${Math.min(extendedTotalMarginPct, 100)}%` }}
                            />
                          </div>
                        </td>
                      )}
                      {columnVisibility.balance && <td className="exchange-header-cell balance-cell">${formatNumber(extendedTotalBalance)}</td>}
                      {columnVisibility.liqPrice && <td className="exchange-header-cell"></td>}
                      {columnVisibility.adl && <td className="exchange-header-cell"></td>}
                      {columnVisibility.leverage && <td className="exchange-header-cell"></td>}
                      {columnVisibility.netExposure && <td className="exchange-header-cell"></td>}
                      {columnVisibility.kevMarginMetric && (
                        <td className={`exchange-header-cell margin-pct-cell ${getMarginClass(extendedKevMarginMetric || 0)}`}>
                          {extendedKevMarginMetric !== null && extendedKevMarginMetric !== undefined 
                            ? `${formatNumber(extendedKevMarginMetric)}%` 
                            : '-'}
                        </td>
                      )}
                    </tr>
                    {expandedExchanges.extended && extendedPositions.map((position, idx) => {
                      const marginClass = getMarginClass(position.margin_percentage || 0);
                      const notional = getNotional(position);
                      return (
                        <tr key={`extended-${position.pair}-${idx}`} className="margin-row">
                          {columnVisibility.exchange && (
                            <td className="exchange-cell">Extended</td>
                          )}
                          {columnVisibility.symbol && (
                            <td className="symbol-cell">
                              <div className="symbol-info">
                                <span className={`side-indicator ${position.side.toLowerCase()}`}></span>
                                <span className="symbol-name">{position.pair}</span>
                              </div>
                            </td>
                          )}
                          {columnVisibility.side && (
                            <td className={`side-cell ${position.side.toLowerCase()}`}>
                              <span className="side-cell-with-bar">
                                <span className="side-bar" aria-hidden="true"></span>
                                <span>{position.side}</span>
                              </span>
                            </td>
                          )}
                          {columnVisibility.marginUsed && (
                            <td className="margin-used-cell">
                              ${formatNumber(position.margin_used)}
                            </td>
                          )}
                          {columnVisibility.marginPct && (
                            <td className={`margin-pct-cell ${marginClass}`}>
                              <div className="margin-pct-value">{formatNumber(position.margin_percentage)}%</div>
                              <div className="margin-bar-container">
                                <div className="margin-bar-threshold"></div>
                                <div 
                                  className={`margin-bar ${marginClass}`}
                                  style={{ width: `${Math.min(position.margin_percentage || 0, 100)}%` }}
                                />
                              </div>
                            </td>
                          )}
                          {columnVisibility.balance && <td className="balance-cell">–</td>}
                          {columnVisibility.liqPrice && (
                            <td className="liq-price-cell">
                              {position.liquidation_price ? `$${formatPrice(position.liquidation_price)}` : '-'}
                            </td>
                          )}
                          {columnVisibility.adl && (
                            <td className="adl-cell">
                              {position.adl !== null && position.adl !== undefined ? `${position.adl}/5` : '-'}
                            </td>
                          )}
                          {columnVisibility.leverage && (
                            <td className="leverage-cell">{position.leverage}x</td>
                          )}
                          {columnVisibility.netExposure && (
                            <td className="net-exposure-cell">
                              ${formatNumber(notional)}
                            </td>
                          )}
                          {columnVisibility.kevMarginMetric && (
                            <td className={`margin-pct-cell ${getMarginClass(position.kev_margin_metric || 0)}`}>
                              {position.kev_margin_metric !== null && position.kev_margin_metric !== undefined 
                                ? `${formatNumber(position.kev_margin_metric)}%` 
                                : '-'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MarginTable;

